import { Component, OnInit, inject } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { VariacaoService } from '../../../../shared/services/apis/produto/variacao.service';
import {
  VariacaoRequest,
  VariacaoValorRequest,
} from '../../../../shared/services/requests/produto/variacao.request';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';

@Component({
  selector: 'app-variacao-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    HeaderComponent,
    MatTooltipModule,
    AlertCardComponent,
    LoadingBoxComponent,
    FooterActionComponent,
    FormsModule,
  ],
  templateUrl: './variacao-cadastro.component.html',
  styleUrl: './variacao-cadastro.component.css',
})
export class VariacaoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public form: FormGroup;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public isEdicao: boolean = false;
  public idVariacao: number = 0;

  valores: VariacaoValorRequest[] = [];

  // announcer = inject(LiveAnnouncer); // Not needed anymore

  get activeValores(): VariacaoValorRequest[] {
    return this.valores.filter(v => !v.excluido);
  }

  constructor(
    private formBuilder: FormBuilder,
    private variacaoService: VariacaoService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.createForm();

    this.route.params.subscribe(params => {
      if (params['idVariacao']) {
        this.idVariacao = +params['idVariacao'];
        this.isEdicao = true;
        this.carregarVariacao();
      } else {
        this.addValor();
      }
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      nome: ['', Validators.required],
      valores: [[]], // Validated via this.activeValores array
    });
  }

  private carregarVariacao(): void {
    this.isLoading = true;
    this.variacaoService.getById(this.idVariacao).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao carregar variação.',
            AlertType.error
          );
          this.voltar();
          return;
        }

        if (res.variacaoProduto) {
          this.form.patchValue({
            nome: res.variacaoProduto.nome,
          });
          // Map existing values to request structure
          this.valores = (res.variacaoProduto.listaValores || []).map(v => ({
            idVariacaoValor: v.idVariacaoValor,
            descricao: v.descricao,
            possuiProdutos: v.possuiProdutos,
            excluido: false,
          }));

          if (this.activeValores.length === 0) {
            this.addValor();
          }
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao carregar a variação.', AlertType.error);
        console.error(err);
        this.voltar();
      },
    });
  }

  addValor(): void {
    this.valores.push({ descricao: '', possuiProdutos: false, excluido: false });
  }

  remove(valor: VariacaoValorRequest): void {
    if (valor.possuiProdutos) {
      this.alertService.show(
        'Atenção',
        'Este valor está vinculado a um produto e não pode ser removido.',
        AlertType.warning
      );
      return;
    }

    const index = this.valores.indexOf(valor);

    if (index >= 0) {
      if (valor.idVariacaoValor) {
        valor.excluido = true;
      } else {
        this.valores.splice(index, 1);
      }

      if (this.activeValores.length === 0) {
        this.addValor();
      }
    }
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Verifique os campos obrigatórios.', AlertType.warning);
      return;
    }

    const valoresPreenchidos = this.activeValores.filter(v => v.descricao && v.descricao.trim().length > 0);

    if (valoresPreenchidos.length === 0) {
      this.alertService.show(
        'Atenção',
        'Adicione pelo menos uma descrição válida para a variação.',
        AlertType.warning
      );
      return;
    }

    this.isSaving = true;
    const request: VariacaoRequest = {
      nome: this.form.get('nome')?.value,
      valores: valoresPreenchidos,
    };

    if (this.isEdicao) {
      this.variacaoService.update(this.idVariacao, request).subscribe({
        next: res => {
          this.tratarRetornoSalvar(res);
        },
        error: err => {
          this.tratarErroSalvar(err);
        },
      });
    } else {
      this.variacaoService.create(request).subscribe({
        next: res => {
          this.tratarRetornoSalvar(res);
        },
        error: err => {
          this.tratarErroSalvar(err);
        },
      });
    }
  }

  private tratarRetornoSalvar(res: any): void {
    this.isSaving = false;
    if (res.isError) {
      this.alertService.show(
        'Atenção',
        res.errorDescription || 'Erro ao salvar variação.',
        AlertType.warning
      );
    } else {
      this.alertService.show('Sucesso', 'Variação salva com sucesso.', AlertType.success);
      this.voltar();
    }
  }

  private tratarErroSalvar(err: any): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
    console.error(err);
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/cadastros/variacoes']);
  }
}
